import React, { useRef, useState, useEffect } from 'react';
import { resolveImageUrl } from '../context/SettingsContext';

const ALLOWED_W = [480, 800, 1280];
const TINY_W = 32;       // ~1-2 KB LQIP placeholder (matches backend ALLOWED_W)
const FADE_MS = 450;     // soft fade / blur duration

const appendWidth = (url, w) =>
  url && url.includes('/api/files/')
    ? `${url}${url.includes('?') ? '&' : '?'}w=${w}`
    : url;

const buildSrcSet = (resolvedUrl) => {
  if (!resolvedUrl || typeof resolvedUrl !== 'string') return null;
  if (!resolvedUrl.includes('/api/files/')) return null;
  return ALLOWED_W
    .map((w) => `${appendWidth(resolvedUrl, w)} ${w}w`)
    .join(', ');
};

/**
 * Drop-in <img> with soft fade-in + LQIP blur-up (Phase 70).
 *
 * Behaviour:
 * 1. Renders a tiny ?w=24 placeholder immediately (≈ 1 KB) — heavily blurred
 *    and slightly scaled, so the layout is filled with a colour-correct
 *    shimmer instead of an empty rectangle.
 * 2. In parallel, preloads the full-resolution variant in JS (using the same
 *    srcSet so the browser picks the right size for the viewport).
 * 3. Once the real image has finished decoding, we swap `src` and animate
 *    `filter: blur(14px) → none` together with `opacity: 0.4 → 1` over 700 ms
 *    for a soft "blur-up" reveal — the visual style used by Medium, Unsplash,
 *    Pinterest etc.
 *
 * Falls back to the previous "just-fade" mode for non-backend URLs, since the
 * resizer endpoint isn't available there.
 */
const ResponsiveImage = ({
  src,
  alt = '',
  sizes = '100vw',
  className = '',
  style,
  width,
  height,
  loading,
  fetchPriority,
  priority,
  fade = true,
  blurUp,
  onLoad,
  ...rest
}) => {
  const resolved = resolveImageUrl(src);
  const isApiFile = !!(resolved && resolved.includes('/api/files/'));
  const srcSet = buildSrcSet(resolved);
  const fullSrc = appendWidth(resolved, 800);
  const tinySrc = appendWidth(resolved, TINY_W);
  const useBlurUp = blurUp !== false && isApiFile;
  const effectiveLoading = loading || (priority ? 'eager' : 'lazy');
  const effectiveFetchPriority = fetchPriority || (priority ? 'high' : undefined);

  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // Preload the full image in JS so we can swap `src` only when ready.
  // Only used in blur-up mode; otherwise the browser handles it natively.
  useEffect(() => {
    if (!useBlurUp || !fullSrc) return undefined;
    let cancelled = false;
    const img = new Image();
    if (srcSet) img.srcset = srcSet;
    img.sizes = sizes;
    img.src = fullSrc;
    const done = () => { if (!cancelled) setLoaded(true); };
    if (img.complete && img.naturalWidth > 0) {
      done();
    } else {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });   // fail-open
    }
    return () => { cancelled = true; };
  }, [fullSrc, srcSet, sizes, useBlurUp]);

  // Non-blur-up: keep the old "fade-in once loaded" behaviour.
  useEffect(() => {
    if (useBlurUp) return;
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [fullSrc, useBlurUp]);

  // Decide what to show right now
  const currentSrc = useBlurUp ? (loaded ? fullSrc : tinySrc) : fullSrc;
  const currentSrcSet = useBlurUp && !loaded ? undefined : (srcSet || undefined);

  // Phase 92 Bugfix: transform:scale(1.04) im Blur-Up-State entfernt — hat auf Mobile
  // Overflow-Clipping durchbrochen (Bild überlappte benachbarte Karten beim Scrollen)
  // und kollidierte mit `group-hover:scale-[1.04]` von Tailwind. Nur filter+opacity animieren.
  // Phase 95: Transform-Transition explizit hinzufügen, damit Tailwinds `group-hover:scale-*`
  // weich (700ms ease-out) statt hart animiert — sonst überschreibt der inline-Style die Tailwind-Klasse.
  const fadeStyle = fade
    ? {
        transition: `filter ${FADE_MS}ms ease-out, opacity ${FADE_MS}ms ease-out, transform 700ms ease-out`,
        filter: useBlurUp && !loaded ? 'blur(14px)' : 'none',
        opacity: loaded ? 1 : (useBlurUp ? 1 : 0),
      }
    : null;

  return (
    <img
      ref={imgRef}
      src={currentSrc}
      alt={alt}
      srcSet={currentSrcSet}
      sizes={currentSrcSet ? sizes : undefined}
      className={className}
      style={{ ...fadeStyle, ...style }}
      width={width}
      height={height}
      loading={effectiveLoading}
      decoding="async"
      fetchPriority={effectiveFetchPriority}
      draggable={false}
      onLoad={(e) => {
        if (!useBlurUp) setLoaded(true);
        onLoad?.(e);
      }}
      {...rest}
    />
  );
};

export default ResponsiveImage;
